import { Check } from 'lucide-react'
import { LuClock, LuCalendarDays, LuTimer } from 'react-icons/lu'

import { cn } from '#/lib/utils'
import type { Plan } from '#/data/plans'
import { AnimatedNumber } from '#/components/common/AnimatedNumber'
import { Link } from '@tanstack/react-router'

type PlanCardProps = {
  plan: Plan
}

// ─── Stat Badge ───────────────────────────────────────────────────────────────

function StatBadge({
  icon: Icon,
  numericValue,
  suffix,
  label,
  featured,
}: {
  icon: React.ElementType
  numericValue: number
  suffix: string
  label: string
  featured?: boolean
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-0.5 rounded-xl px-3 py-2.5 text-center',
        featured ? 'bg-white/10 text-white' : 'bg-primary-50 text-neutral-800',
      )}
    >
      <Icon
        className={cn(
          'mb-0.5 h-4 w-4',
          featured ? 'text-primary-300' : 'text-primary-600',
        )}
      />
      <span className="text-sm font-bold leading-tight tabular-nums">
        <AnimatedNumber value={numericValue} />
        {suffix}
      </span>
      <span
        className={cn(
          'text-[10px] font-medium uppercase tracking-wide leading-tight',
          featured ? 'text-white/60' : 'text-neutral-500',
        )}
      >
        {label}
      </span>
    </div>
  )
}

// ─── Plan Card ────────────────────────────────────────────────────────────────

export default function PlanCard({ plan }: PlanCardProps) {
  return (
    <div
      className={cn(
        'relative flex w-full flex-col rounded-3xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl',
        plan.featured
          ? [
              'noir-section border-primary-500/40 text-white',
              'shadow-[0_0_40px_rgba(255,182,6,0.12)]',
              'hover:shadow-[0_8px_50px_rgba(255,182,6,0.22)]',
            ]
          : 'border-neutral-100 bg-white shadow-sm hover:shadow-neutral-200/80',
      )}
    >
      {/* Badge */}
      {plan.badge && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-4 py-1 text-xs font-bold uppercase tracking-widest text-neutral-900 shadow-md">
            ★ {plan.badge}
          </span>
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────── */}
      <div
        className={cn(
          'rounded-t-3xl px-7 pt-8 pb-6',
          plan.featured
            ? 'border-b border-white/10'
            : 'border-b border-neutral-100',
        )}
      >
        {/* Plan name */}
        <p
          className={cn(
            'mb-4 text-xs font-bold uppercase tracking-[0.2em]',
            plan.featured ? 'text-primary-400' : 'text-primary-600',
          )}
        >
          {plan.name}
        </p>

        {/* Price — animated */}
        <div className="flex items-end gap-1">
          <span
            className={cn(
              'text-5xl font-bold leading-none tabular-nums',
              plan.featured ? 'text-white' : 'text-neutral-900',
            )}
          >
            $
            <AnimatedNumber
              value={plan.pricePerMonth}
              stiffness={140}
              damping={20}
            />
          </span>
          <span
            className={cn(
              'mb-1 text-sm font-medium',
              plan.featured ? 'text-white/60' : 'text-neutral-500',
            )}
          >
            / month
          </span>
        </div>

        {/* Stats row — all numbers animated */}
        <div className="mt-5 grid grid-cols-3 gap-2">
          <StatBadge
            icon={LuClock}
            numericValue={plan.hoursPerMonth}
            suffix=" Hrs"
            label="per month"
            featured={plan.featured}
          />
          <StatBadge
            icon={LuTimer}
            numericValue={plan.classMinutes}
            suffix=" Min"
            label="per class"
            featured={plan.featured}
          />
          <StatBadge
            icon={LuCalendarDays}
            numericValue={plan.daysPerWeek}
            suffix="×"
            label="per week"
            featured={plan.featured}
          />
        </div>
      </div>

      {/* ── Features ───────────────────────────────────────── */}
      <ul className="flex flex-1 flex-col gap-3 px-7 py-6">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3">
            <span
              className={cn(
                'mt-0.5 flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full',
                plan.featured
                  ? 'bg-primary-500/25 text-primary-400'
                  : 'bg-secondary-100 text-secondary-600',
              )}
            >
              <Check className="h-2.5 w-2.5 stroke-[3]" />
            </span>
            <span
              className={cn(
                'text-sm leading-snug',
                plan.featured ? 'text-white/80' : 'text-neutral-700',
              )}
            >
              {feature}
            </span>
          </li>
        ))}
      </ul>

      {/* ── CTA ────────────────────────────────────────────── */}
      <div className="px-7 pb-8">
        <Link
          to="/subscribe"
          search={{ plan: plan.duration }}
          id={`plan-trial-${plan.id}`}
          className={cn(
            'flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-bold tracking-wide transition-all duration-200',
            plan.featured
              ? [
                  'bg-primary-500 text-neutral-900',
                  'hover:bg-primary-400 hover:shadow-[0_4px_20px_rgba(255,182,6,0.4)]',
                ]
              : [
                  'border-2 border-primary-500 text-primary-700',
                  'hover:bg-primary-500 hover:text-neutral-900',
                ],
          )}
        >
          Enroll Now — Free Trial
        </Link>
      </div>
    </div>
  )
}
